package one.healthium.tasks.data.mapper

import one.healthium.common.mapper.ColorMapper
import one.healthium.db.DailyTaskEntity
import one.healthium.tasks.data.dto.DailyTaskInfoDto
import one.healthium.tasks.data.dto.DailyTasksDto
import one.healthium.tasks.data.mapper.DailyTaskMapper.Companion.TYPE_DO_WORKOUT
import one.healthium.tasks.data.mapper.DailyTaskMapper.Companion.TYPE_LOG_FOOD
import one.healthium.tasks.data.mapper.DailyTaskMapper.Companion.TYPE_LOG_SYMPTOMS
import one.healthium.tasks.data.mapper.DailyTaskMapper.Companion.TYPE_LOG_WATER
import one.healthium.tasks.data.mapper.DailyTaskMapper.Companion.TYPE_LOG_WEIGHT
import one.healthium.tasks.data.mapper.DailyTaskMapper.Companion.TYPE_READ_INSIGHT
import one.healthium.tasks.domain.DailyTaskInfoModel
import one.healthium.tasks.domain.DailyTaskModel
import one.healthium.tasks.domain.DailyTaskType
import kotlin.uuid.Uuid

internal class DailyTaskMapper(
    private val colorMapper: ColorMapper
) {

    fun dtoToModel(dto: DailyTasksDto): List<List<DailyTaskInfoModel>> {
        val configColors = dto.config.colors
        return dto.days.map { dayDto ->
            dayDto.tasks.mapNotNull { mapTaskInfo(it, configColors) }
        }
    }

    fun entityToModel(entity: DailyTaskEntity): DailyTaskModel? {
        val type = mapType(entity.type, entity.payload, entity.payload) ?: return null
        return DailyTaskModel(
            id = entity.id,
            info = DailyTaskInfoModel(
                name = entity.name,
                type = type,
                color = entity.color,
            ),
            completionCount = entity.completionCount.toInt(),
        )
    }

    fun createInfoDtos(dailyTasksForToday: List<DailyTaskInfoModel>, date: String): List<DailyTaskEntity> {
        return dailyTasksForToday.map {
            DailyTaskEntity(
                id = Uuid.random().toHexString(),
                name = it.name,
                type = it.type.name(),
                payload = when (it.type) {
                    is DailyTaskType.ReadInsight -> it.type.id
                    is DailyTaskType.DoWorkout -> it.type.id
                    else -> null
                },
                color = it.color,
                completionCount = 0L,
                date = date,
            )
        }
    }

    private fun mapTaskInfo(dto: DailyTaskInfoDto, configColors: Map<String, String>): DailyTaskInfoModel? {
        val taskType = mapType(dto.type, dto.insightId, dto.workoutId) ?: return null
        return DailyTaskInfoModel(
            name = dto.title,
            type = taskType,
            color = dto.color?.let(colorMapper::parseColor) ?: configColors[dto.type]?.let(colorMapper::parseColor) ?: 0L,
        )
    }

    private fun mapType(type: String, insightId: String?, workoutId: String?): DailyTaskType? {
        return when (type) {
            TYPE_LOG_WEIGHT -> DailyTaskType.LogWeight
            TYPE_LOG_WATER -> DailyTaskType.LogWater
            TYPE_READ_INSIGHT -> {
                if (insightId.isNullOrBlank()) return null
                DailyTaskType.ReadInsight(insightId)
            }

            TYPE_LOG_FOOD -> DailyTaskType.LogFood
            TYPE_DO_WORKOUT -> {
                if (workoutId.isNullOrBlank()) return null
                DailyTaskType.DoWorkout(workoutId)
            }

            TYPE_LOG_SYMPTOMS -> DailyTaskType.LogSymptoms

            else -> return null
        }
    }

    companion object {
        internal const val TYPE_LOG_WEIGHT = "log_weight"
        internal const val TYPE_LOG_WATER = "log_water"
        internal const val TYPE_READ_INSIGHT = "read_insight"
        internal const val TYPE_LOG_FOOD = "log_food"
        internal const val TYPE_DO_WORKOUT = "do_workout"
        internal const val TYPE_LOG_SYMPTOMS = "log_symptoms"
    }
}

internal fun DailyTaskType.name(): String = when (this) {
    is DailyTaskType.LogWeight -> TYPE_LOG_WEIGHT
    is DailyTaskType.LogWater -> TYPE_LOG_WATER
    is DailyTaskType.ReadInsight -> TYPE_READ_INSIGHT
    is DailyTaskType.LogFood -> TYPE_LOG_FOOD
    is DailyTaskType.DoWorkout -> TYPE_DO_WORKOUT
    DailyTaskType.LogSymptoms -> TYPE_LOG_SYMPTOMS
}
